import { useMemo } from 'react'
import { stickerImageUrl } from '@/lib/markdown'
import type { Sticker, StickerItem } from '@/types/api'
import styles from './MediaPicker.module.css'

/** Почему стикеры других серверов отсюда не отправить: нет Кличат Плюса или сервер их запретил. */
export type StickerLock = 'plus' | 'forbidden' | null

interface Props {
  /**
   * Стикеры по серверам: текущий сервер первым; в ЛС — все серверы пользователя.
   * locked — стикеры другого сервера, которые в этом канале не отправить (см. lock).
   */
  groups: { guild: string; stickers: Sticker[]; locked?: boolean }[]
  query: string
  lock?: StickerLock
  /** Открыть «Плюс и бусты»: Плюс дают за приглашённых друзей. */
  onGetPlus?: (() => void) | undefined
  onPick: (sticker: StickerItem) => void
}

/** Содержимое вкладки «Стикеры»: поиск по имени, описанию и тегам. */
export function StickerGrid({ groups, query, lock = null, onGetPlus, onPick }: Props) {
  const q = query.trim().toLowerCase()
  const visible = useMemo(
    () =>
      groups
        .map((g) => ({ ...g, stickers: q ? g.stickers.filter((s) => `${s.name} ${s.description} ${s.tags}`.toLowerCase().includes(q)) : g.stickers }))
        .filter((g) => g.stickers.length),
    [groups, q],
  )
  const total = groups.reduce((n, g) => n + g.stickers.length, 0)
  const firstLocked = visible.findIndex((g) => g.locked)

  if (total === 0) return <p className={styles.empty}>Стикеров пока нет. Добавьте их в настройках сервера → «Стикеры».</p>
  if (!visible.length) return <p className={styles.empty}>Ничего не найдено</p>

  return (
    <>
      {visible.map((g, i) => (
        <div key={g.guild} className={styles.group}>
          {i === firstLocked && lock ? (
            <div className={styles.lockNote}>
              {lock === 'plus' ? (
                <>
                  <span>Стикеры других серверов — с Кличат Плюсом. Его дают бесплатно за двух приглашённых друзей.</span>
                  {onGetPlus ? <button type="button" onClick={onGetPlus}>Как получить Плюс</button> : null}
                </>
              ) : (
                <span>На этом сервере стикеры других серверов выключены.</span>
              )}
            </div>
          ) : null}
          <div className={styles.groupTitle}>{g.guild}</div>
          <div className={styles.stickerGrid}>
            {g.stickers.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`${styles.cell} ${styles.stickerCell} ${g.locked ? styles.stickerLocked : ''}`}
                data-tooltip={s.description ? `${s.name} — ${s.description}` : s.name}
                disabled={g.locked}
                onClick={() => onPick({ id: s.id, name: s.name, format_type: s.format_type })}
              >
                <img src={s.url ?? stickerImageUrl(s)} alt={s.name} className={styles.stickerImg} draggable={false} loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
